'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import Image from '@/components/media/SiteImage';
import { Menu, ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import { navItems, isDropdown } from '@/data/navigation';
import MobileMenu from '@/components/layout/MobileMenu';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';

export default function Navbar() {
  const t = useTranslations('Nav');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (label: string) => {
    if (!window.matchMedia("(hover: hover)").matches) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <>
      <nav
        className={cn(
          'fixed top-[var(--weather-bar-height)] left-0 right-0 z-50 bg-[#FAFAF5] shadow-sm'
        )}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-[72px]">
            {/* Logo */}
            <Link href="/" className="flex-shrink-0">
              <Image
                src="/images/logo.png"
                alt="Kettura Finland"
                width={200}
                height={200}
                className="h-[48px] w-[48px]"
                priority
              />
            </Link>

            {/* Center Navigation - Desktop */}
            <div className="hidden xl:flex items-center gap-1">
              {navItems.map((item) =>
                isDropdown(item) ? (
                  <div
                    key={t(item.tKey)}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(item.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      type="button"
                      aria-expanded={openDropdown === item.label}
                      onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                      onKeyDown={(event) => { if (event.key === "Escape") setOpenDropdown(null); }}
                      className={cn(
                        'flex items-center gap-1 min-h-11 px-2 py-2 rounded-md font-[family-name:var(--font-outfit)] text-xs font-medium uppercase tracking-[0.1em]',
                        'transition-colors duration-300 hover:text-[#9B7B3A]',
                        openDropdown === item.label
                          ? 'text-[#9B7B3A]'
                          : 'text-[#1A1A2E]'
                      )}
                    >
                      {t(item.tKey)}
                      <ChevronDown
                        className={cn(
                          'w-3 h-3 transition-transform duration-200',
                          openDropdown === item.label && 'rotate-180'
                        )}
                      />
                    </button>

                    {/* Dropdown Panel */}
                    {openDropdown === item.label && (
                      <div className="absolute top-full left-0 mt-1 min-w-[220px] rounded-xl py-2 shadow-xl border bg-white border-[#F0EDE5]">
                        {item.items.map((subItem) => (
                          <Link
                            key={subItem.href}
                            href={subItem.href}
                            onClick={() => setOpenDropdown(null)}
                            className="block px-4 py-2.5 font-[family-name:var(--font-outfit)] text-sm font-light tracking-wide transition-colors duration-200 text-[#5A5A6E] hover:text-[#1A1A2E] hover:bg-[#F0EDE5]"
                          >
                            {t(subItem.tKey)}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="inline-flex min-h-11 items-center px-2 py-2 font-[family-name:var(--font-outfit)] text-xs font-medium uppercase tracking-[0.1em] transition-colors duration-300 hover:text-[#9B7B3A] text-[#1A1A2E]"
                  >
                    {t(item.tKey)}
                  </Link>
                )
              )}
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-3">
              {/* Language switcher - Desktop */}
              <div className="hidden xl:block">
                <LanguageSwitcher />
              </div>

              {/* Book Now Button - Desktop */}
              <Link
                href="/cabins"
                className="hidden sm:inline-flex items-center min-h-11 px-5 py-2.5 rounded-full bg-[#9B7B3A] text-white font-[family-name:var(--font-outfit)] text-xs font-semibold uppercase tracking-[0.1em] hover:bg-[#C4A95E] transition-colors duration-300"
              >
                {t('bookNow')}
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileOpen(true)}
                className="xl:hidden p-2.5 min-h-11 min-w-11 rounded-md transition-colors duration-200 text-[#1A1A2E] hover:bg-[#F0EDE5]"
                aria-label="Open menu"
                aria-expanded={mobileOpen}
                aria-haspopup="dialog"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu isOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}
